import Swal from 'sweetalert2'

const PAGE_POPUP_CLASSES = {
  popup: 'cl-swal-popup',
  icon: 'cl-swal-icon',
  title: 'cl-swal-title',
  htmlContainer: 'cl-swal-text',
  actions: 'cl-swal-actions',
}

/** Single OK button popup (same look as showError). */
const OK_BUTTON_POPUP = {
  buttonsStyling: false,
  customClass: {
    ...PAGE_POPUP_CLASSES,
    confirmButton: 'cl-admin-btn cl-admin-btn-primary',
  },
}

/**
 * Confirm popup styled like admin pages (same font, sizes and cl-admin-btn buttons).
 * @returns {Promise<boolean>} true when the user confirms
 */
export const confirmAction = async ({
  title = 'Are you sure?',
  text = '',
  html = '',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  icon = 'question',
} = {}) => {
  const result = await Swal.fire({
    title,
    ...(html ? { html } : { text }),
    icon,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    reverseButtons: true,
    focusCancel: true,
    buttonsStyling: false,
    customClass: {
      ...PAGE_POPUP_CLASSES,
      confirmButton: 'cl-admin-btn cl-admin-btn-primary',
      cancelButton: 'cl-admin-btn cl-admin-btn-ghost',
    },
  })
  return Boolean(result.isConfirmed)
}

/**
 * Confirm popup with a text input (e.g. amount paid).
 * @returns {Promise<string|null>} entered value, or null when cancelled
 */
export const promptAction = async ({
  title = 'Enter a value',
  text = '',
  html = '',
  inputLabel = '',
  inputValue = '',
  inputPlaceholder = '',
  confirmText = 'Save',
  cancelText = 'Cancel',
  icon = 'question',
} = {}) => {
  const result = await Swal.fire({
    title,
    ...(html ? { html } : { text }),
    icon,
    input: 'text',
    inputLabel,
    inputValue,
    inputPlaceholder,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    reverseButtons: true,
    focusCancel: true,
    buttonsStyling: false,
    customClass: {
      ...PAGE_POPUP_CLASSES,
      confirmButton: 'cl-admin-btn cl-admin-btn-primary',
      cancelButton: 'cl-admin-btn cl-admin-btn-ghost',
    },
  })
  if (!result.isConfirmed) return null
  return result.value
}

/**
 * Confirm delete action
 * @param {string} itemName - Name of the item to delete
 * @returns {Promise} - Promise that resolves if confirmed, rejects if cancelled
 */
export const confirmDelete = (itemName) => {
  return Swal.fire({
    title: 'Are you sure?',
    text: `Delete ${itemName}? This action cannot be undone.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it!',
    cancelButtonText: 'Cancel'
  })
}

/**
 * Confirm approve action
 * @param {string} itemName - Name of the item to approve
 * @returns {Promise} - Promise that resolves if confirmed, rejects if cancelled
 */
export const confirmApprove = (itemName) => {
  return Swal.fire({
    title: 'Approve Item?',
    text: `Approve ${itemName}?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#28a745',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, approve it!',
    cancelButtonText: 'Cancel'
  })
}

/**
 * Confirm reject action
 * @param {string} itemName - Name of the item to reject
 * @returns {Promise} - Promise that resolves if confirmed, rejects if cancelled
 */
export const confirmReject = (itemName) => {
  return Swal.fire({
    title: 'Reject Item?',
    text: `Reject ${itemName}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, reject it!',
    cancelButtonText: 'Cancel'
  })
}

/**
 * Confirm bulk delete action
 * @param {number} count - Number of items to delete
 * @returns {Promise} - Promise that resolves if confirmed, rejects if cancelled
 */
export const confirmBulkDelete = (count) => {
  return Swal.fire({
    title: 'Are you sure?',
    text: `Delete ${count} item(s)? This action cannot be undone.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete them!',
    cancelButtonText: 'Cancel'
  })
}

export const confirmDeactivate = (itemName) => {
  return Swal.fire({
    title: 'Deactivate user?',
    text: `Deactivate ${itemName}? They will no longer be able to sign in.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, deactivate',
    cancelButtonText: 'Cancel'
  })
}

export const confirmArchiveSession = (sessionTitle) => {
  const label = (sessionTitle || '').trim() || 'this session'
  return Swal.fire({
    title: 'Archive session?',
    text: `Archive “${label}”? It will be removed from your active sessions list.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#0066cc',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, archive',
    cancelButtonText: 'Cancel'
  })
}

export const confirmActivate = (itemName) => {
  return Swal.fire({
    title: 'Activate user?',
    text: `Activate ${itemName}? They will be able to sign in again.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#28a745',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, activate',
    cancelButtonText: 'Cancel'
  })
}

/**
 * Confirm SaaS workspace package upgrade with immediate proration / payment amount.
 * @param {object} preview - API preview payload
 */
export const confirmPackageUpgradePayment = (preview = {}) => {
  const label = (preview.target_plan_name || preview.target_plan_code || 'the selected package')
  const amount = preview.amount_due_now_zar || preview.proration?.amount_due_now_zar || '0.00'
  const summary =
    preview.proration?.summary ||
    `A prorated charge of R${amount} is due immediately for the rest of this billing period.`
  const needsPayfast = Number(amount) > 0
  return Swal.fire({
    title: `Upgrade to ${label}?`,
    html: `
      <p class="mb-2 text-start">${summary}</p>
      <p class="mb-0 text-start"><strong>Amount due now: R${amount}</strong></p>
      <p class="small text-muted text-start mt-2 mb-0">
        ${
          needsPayfast
            ? 'You will complete payment securely on PayFast. Your package updates after PayFast confirms.'
            : 'Confirming applies the higher package immediately (no payment due).'
        }
      </p>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#0066cc',
    cancelButtonColor: '#6c757d',
    confirmButtonText: needsPayfast ? `Pay R${amount} on PayFast` : 'Confirm upgrade',
    cancelButtonText: 'Cancel',
  })
}

/**
 * Confirm SaaS add-on purchase (team seat or stores pack).
 * @param {{ label?: string, amountZar?: number|string, hint?: string }} opts
 */
export const confirmAddonPurchasePayment = (opts = {}) => {
  const label = opts.label || 'this add-on'
  const amount = Number(opts.amountZar)
  const amountLabel = Number.isFinite(amount)
    ? amount.toLocaleString('en-ZA', { maximumFractionDigits: 0 })
    : String(opts.amountZar ?? '0')
  const needsPayfast = Number.isFinite(amount) ? amount > 0 : Number(opts.amountZar) > 0
  const allowEft = opts.allowEft !== false
  const hint =
    opts.hint ||
    (label.toLowerCase().includes('store')
      ? 'Adds store capacity and the seats included with a stores pack for the rest of this billing period.'
      : 'Adds one extra inbox login for the rest of this billing period.')
  return Swal.fire({
    title: `Buy ${label}?`,
    html: `
      <p class="mb-2 text-start">${hint}</p>
      <p class="mb-0 text-start"><strong>Amount due now: R${amountLabel}</strong></p>
      <p class="small text-muted text-start mt-2 mb-0">
        ${
          needsPayfast
            ? allowEft
              ? 'Pay on PayFast now, or pay by EFT and send proof on Usage & billing → Payments. Capacity updates after payment is confirmed.'
              : 'You will complete payment securely on PayFast. Capacity updates after PayFast confirms.'
            : 'Confirming activates the add-on immediately (no payment due).'
        }
      </p>
    `,
    icon: 'question',
    showCancelButton: true,
    showDenyButton: needsPayfast && allowEft,
    denyButtonText: 'Pay by EFT',
    confirmButtonText: needsPayfast ? `Pay R${amountLabel} on PayFast` : 'Confirm purchase',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    focusCancel: true,
    buttonsStyling: false,
    customClass: {
      ...PAGE_POPUP_CLASSES,
      confirmButton: 'cl-admin-btn cl-admin-btn-primary',
      denyButton: 'cl-admin-btn cl-admin-btn-ghost',
      cancelButton: 'cl-admin-btn cl-admin-btn-ghost',
    },
  })
}

/**
 * Confirm SaaS workspace package downgrade, optionally selecting seats/stores to deactivate.
 * @param {object} preview - API preview payload
 * @returns {Promise<{isConfirmed: boolean, value?: {deactivate_user_ids: number[], terminate_store_ids: number[]}}>}
 */
export const confirmPackageDowngradeWithPicks = async (preview = {}) => {
  const label = preview.target_plan_name || preview.target_plan_code || 'the previous package'
  const capacity = preview.capacity || {}
  const seatsNeeded = Number(capacity.seats_to_remove || 0)
  const storesNeeded = Number(capacity.stores_to_remove || 0)
  const seatCandidates = Array.isArray(capacity.seat_candidates) ? capacity.seat_candidates : []
  const storeCandidates = Array.isArray(capacity.store_candidates) ? capacity.store_candidates : []
  const credit = preview.proration?.credit_zar || '0.00'
  const creditLine =
    Number(credit) > 0
      ? `<p class="small text-muted text-start">Estimated unused-time credit: R${credit}</p>`
      : ''

  if (!seatsNeeded && !storesNeeded) {
    return Swal.fire({
      title: `Downgrade to ${label}?`,
      html: `
        <p class="text-start mb-2">Seats, stores, and features will return to that package.</p>
        ${creditLine}
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, downgrade',
      cancelButtonText: 'Cancel',
    }).then((result) => ({
      ...result,
      value: { deactivate_user_ids: [], terminate_store_ids: [] },
    }))
  }

  const seatChecks = seatCandidates
    .map(
      (u) => `
      <label class="d-flex align-items-start gap-2 text-start mb-1">
        <input type="checkbox" class="mt-1 swal-seat-pick" value="${u.id}" />
        <span><strong>${u.name || u.email}</strong><br/><span class="small text-muted">${u.email || ''}${u.user_type ? ` · ${u.user_type}` : ''}</span></span>
      </label>`,
    )
    .join('')
  const storeChecks = storeCandidates
    .map(
      (s) => `
      <label class="d-flex align-items-start gap-2 text-start mb-1">
        <input type="checkbox" class="mt-1 swal-store-pick" value="${s.id}" />
        <span><strong>${s.name}</strong>${s.email ? `<br/><span class="small text-muted">${s.email}</span>` : ''}</span>
      </label>`,
    )
    .join('')

  const result = await Swal.fire({
    title: `Downgrade to ${label}?`,
    html: `
      <p class="text-start mb-2">
        This package allows fewer seats/stores. Choose what to deactivate before continuing.
      </p>
      ${
        seatsNeeded
          ? `<p class="text-start mb-1"><strong>Select ${seatsNeeded} team seat(s) to deactivate</strong></p>
             <div class="text-start mb-3" style="max-height:160px;overflow:auto;border:1px solid #e2e8f0;border-radius:8px;padding:8px;">${seatChecks || '<em>No candidates</em>'}</div>`
          : ''
      }
      ${
        storesNeeded
          ? `<p class="text-start mb-1"><strong>Select ${storesNeeded} store(s) to terminate</strong></p>
             <div class="text-start mb-2" style="max-height:160px;overflow:auto;border:1px solid #e2e8f0;border-radius:8px;padding:8px;">${storeChecks || '<em>No candidates</em>'}</div>`
          : ''
      }
      ${creditLine}
    `,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d97706',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Deactivate & downgrade',
    cancelButtonText: 'Cancel',
    width: '36em',
    preConfirm: () => {
      const seatIds = Array.from(document.querySelectorAll('.swal-seat-pick:checked')).map((el) =>
        Number(el.value),
      )
      const storeIds = Array.from(document.querySelectorAll('.swal-store-pick:checked')).map((el) =>
        Number(el.value),
      )
      if (seatIds.length !== seatsNeeded) {
        Swal.showValidationMessage(`Select exactly ${seatsNeeded} team seat(s).`)
        return false
      }
      if (storeIds.length !== storesNeeded) {
        Swal.showValidationMessage(`Select exactly ${storesNeeded} store(s).`)
        return false
      }
      return { deactivate_user_ids: seatIds, terminate_store_ids: storeIds }
    },
  })
  return result
}

/** @deprecated Prefer confirmPackageUpgradePayment(preview) */
export const confirmPackageUpgrade = (packageLabel) => {
  return confirmPackageUpgradePayment({ target_plan_name: packageLabel, amount_due_now_zar: '—' })
}

/** @deprecated Prefer confirmPackageDowngradeWithPicks(preview) */
export const confirmPackageDowngrade = (packageLabel) => {
  return confirmPackageDowngradeWithPicks({ target_plan_name: packageLabel, capacity: {} })
}

export const confirmBulkDeactivate = (count) => {
  return Swal.fire({
    title: 'Deactivate users?',
    text: `Deactivate ${count} active user(s)? They will no longer be able to sign in.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, deactivate',
    cancelButtonText: 'Cancel'
  })
}

export const confirmBulkActivate = (count) => {
  return Swal.fire({
    title: 'Activate users?',
    text: `Activate ${count} inactive user(s)? They will be able to sign in again.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#28a745',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Yes, activate',
    cancelButtonText: 'Cancel'
  })
}

/**
 * Show success message
 * @param {string} message - Success message
 */
export const showSuccess = (message) => {
  return Swal.fire({
    icon: 'success',
    title: 'Success!',
    text: message,
    timer: 5000,
    showConfirmButton: false,
    customClass: PAGE_POPUP_CLASSES,
  })
}

/**
 * Show error message
 * @param {string} message - Error message
 */
export const showError = (message) => {
  return Swal.fire({
    icon: 'error',
    title: 'Error!',
    text: message,
    ...OK_BUTTON_POPUP,
  })
}

/** Labels for common Django REST Framework field names (snake_case keys). */
const DRF_FIELD_LABELS = {
  product_group_id: 'Product group',
  variants: 'Variants',
  variants_json: 'Variants',
  sku: 'SKU',
  email: 'Email',
  phone_number: 'Phone number',
  password: 'Password',
  password_confirm: 'Confirm password',
  first_name: 'First name',
  last_name: 'Last name',
  user_type_id: 'User type',
  non_field_errors: 'Registration',
}

/**
 * Format DRF validation payload (field -> string[] or string) as plain text with line breaks.
 * @param {unknown} data - Typically axios error.response.data
 * @returns {string|null} Message or null if nothing usable was found
 */
export function formatDrfErrorMessage(data) {
  if (data == null) return null
  if (typeof data === 'string') {
    const trimmed = data.trim()
    if (!trimmed) return null
    // Django DEBUG HTML / other non-JSON bodies — never show raw page source
    if (/^\s*<(!DOCTYPE|html|head|title)\b/i.test(trimmed) || trimmed.includes('NameError at ')) {
      return null
    }
    return trimmed
  }
  if (typeof data !== 'object') return String(data)

  if (typeof data.detail === 'string' && data.detail.trim()) {
    const d = data.detail.trim()
    if (/^\s*</.test(d) || d.includes('NameError at ')) return null
    return d
  }
  if (Array.isArray(data.detail) && data.detail.length) {
    return data.detail.map((x) => String(x)).join('\n')
  }

  const lines = []
  for (const [key, val] of Object.entries(data)) {
    if (key === 'detail') continue
    const label = DRF_FIELD_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    if (Array.isArray(val)) {
      val.forEach((msg) => {
        if (msg != null && String(msg).trim()) lines.push(`${label}: ${String(msg).trim()}`)
      })
    } else if (val != null && String(val).trim()) {
      lines.push(`${label}: ${String(val).trim()}`)
    }
  }
  if (lines.length) return lines.join('\n\n')
  return null
}

/**
 * Same as formatDrfErrorMessage but as HTML list for SweetAlert `html` option.
 * @param {unknown} data
 * @returns {string|null}
 */
export function formatDrfErrorHtml(data) {
  const text = formatDrfErrorMessage(data)
  if (!text) return null
  const items = text.split(/\n\n+/).map((block) => {
    const safe = block
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
    return `<li class="text-start">${safe}</li>`
  })
  return `<ul class="mb-0 ps-3 text-start" style="max-width:100%">${items.join('')}</ul>`
}

/**
 * Show SweetAlert for API errors (DRF 400 field errors, detail string, or generic).
 * @param {unknown} error - Axios error or similar with optional response.data
 * @param {string} [title='Something went wrong'] - Alert title
 */
export function showApiError(error, title = 'Something went wrong') {
  const status = error?.response?.status
  if (status === 403) {
    const data = error?.response?.data
    const detail =
      typeof data?.detail === 'string' && data.detail.trim() && !/^\s*</.test(data.detail)
        ? data.detail.trim()
        : 'You do not have permission to perform this action.'
    return Swal.fire({
      icon: 'warning',
      title: 'Permission denied',
      text: detail,
      confirmButtonText: 'OK',
      ...OK_BUTTON_POPUP,
    })
  }

  const data = error?.response?.data
  const html = formatDrfErrorHtml(data)
  const textFallback = formatDrfErrorMessage(data)
  const message =
    html ? null : textFallback || error?.message || 'Please try again.'

  return Swal.fire({
    icon: 'error',
    title,
    ...(html ? { html } : { text: message }),
    confirmButtonText: 'OK',
    ...OK_BUTTON_POPUP,
  })
}

/**
 * Show info message
 * @param {string} message - Info message
 */
export const showInfo = (message) => {
  return Swal.fire({
    icon: 'info',
    title: 'Info',
    text: message,
    ...OK_BUTTON_POPUP,
  })
}

let pendingAlertDepth = 0

/**
 * Non-closeable loading overlay for mutating API calls without local spinners.
 * Ref-counted so nested/concurrent withPendingAlert calls share one dialog.
 */
export function showPendingAlert(message = 'Please wait…') {
  if (pendingAlertDepth === 0) {
    Swal.fire({
      title: message,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      showCancelButton: false,
      didOpen: () => {
        Swal.showLoading()
      },
    })
  }
  pendingAlertDepth += 1
}

export function hidePendingAlert() {
  pendingAlertDepth = Math.max(0, pendingAlertDepth - 1)
  if (pendingAlertDepth === 0 && Swal.isVisible()) {
    Swal.close()
  }
}

/**
 * Run async work behind a blocking pending alert.
 * @param {() => Promise<unknown>} fn
 * @param {string} [message='Please wait…']
 */
export async function withPendingAlert(fn, message = 'Please wait…') {
  showPendingAlert(message)
  try {
    return await fn()
  } finally {
    hidePendingAlert()
  }
}

